import { useMemo } from 'react'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useTechnicianPageStore } from './useTechnicianPageStore'
import { toChunks } from '@/utils'
import {
  useFullPartsBOM,
  useFullToolsBOM,
} from '@/pages/DocumentPage/components/OperationDocument/hooks/useBOM'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { MAX_PARTS_PER_PAGE } from '@/pages/DocumentPage/components/BillOfMaterialsDocument'

import type { Step } from '@/services/queries/operation_steps/types'
import type { ToolsBillOfMaterials, BillOfMaterials } from '@/state'

type StaticTechPage = {
  id: string
  type: string
  rows?: BillOfMaterials | ToolsBillOfMaterials
  order_number?: number
  created?: string
}

export type Page = Step &
  StaticTechPage & {
    subPageId?: string | null
    subPageOrder?: string
    subPageName?: string
  }

const staticPages: StaticTechPage[] = [{ id: 'title', type: 'title' }]

/**
 * Finds Current Techician Page
 */

const useCurrentPage = ({
  currentPageId,
  pages,
}: {
  currentPageId?: string | null
  pages: Page[]
}) => {
  return useMemo(() => {
    if (!currentPageId) return staticPages[0] as Page

    const subPage = pages.find((p) => p.subPageId === currentPageId)
    const opPage = pages.find((p) => p.id === currentPageId)
    const page = subPage || opPage

    if (page) return page as Page

    return staticPages[0] as Page
  }, [currentPageId, pages])
}

/**
 * Finds Next Techician Page
 */

const useNextPage = ({
  currentPage,
  pages,
}: {
  currentPage: Page
  pages: Page[]
}) => {
  return useMemo(() => {
    if (!currentPage) return null
    const index = pages.findIndex(
      (s) => s.id === currentPage.id && s.subPageId === currentPage.subPageId,
    )
    if (index >= 0 && index + 1 < pages.length) {
      return pages[index + 1]
    }

    return null
  }, [currentPage, pages])
}

/**
 * Finds Previous Techician Page
 */

const usePrevPage = ({
  currentPage,
  pages,
}: {
  currentPage: Page
  pages: Page[]
}) => {
  return useMemo(() => {
    if (!currentPage) return null

    const index = pages.findIndex(
      (s) => s.id === currentPage.id && s.subPageId === currentPage.subPageId,
    )
    if (index >= 0 && index - 1 >= 0) {
      return pages[index - 1]
    }

    return null
  }, [currentPage, pages])
}

/**
 * Finds Previous Techician Page
 */

const useCurrentPageIndex = ({
  currentPage,
  pages,
}: {
  currentPage: Page
  pages: Page[]
}) => {
  return useMemo(() => {
    if (!currentPage) return -1

    return pages.findIndex(
      (s) => s.id === currentPage.id && s.subPageId === currentPage.subPageId,
    )
  }, [currentPage, pages])
}

export const useTechnicianPages = () => {
  const { data } = useDocumentPageQuery()
  const { isLoading, steps } = useOperationSteps()

  const flattenedSteps = useMemo(
    () =>
      steps
        .flatMap((step: any) => {
          const orderNumbers = [step.order_number]
          const isSubOperation = !!step.parent
          let p = step.parent
          while (p) {
            const parentStep = steps.find((s) => s.id === p)
            if (parentStep) {
              orderNumbers.unshift(parentStep.order_number)
            }
            p = parentStep?.parent
          }

          const order = orderNumbers.join('.')

          const subPages = (step?.template_values?.subPages || []) as string[]
          const hiddenSubPages = new Set<string>(
            (step?.template_values?.hiddenSubPages as string[] | undefined) ??
              [],
          )
          // Filter out hidden subpages in technician mode
          const visibleSubPages = subPages.filter(
            (subPageId) => !hiddenSubPages.has(subPageId),
          )

          if (subPages.length > 0 && visibleSubPages.length === 0) {
            return []
          }

          if (visibleSubPages.length > 0) {
            return visibleSubPages.map((subPageId, i) => {
              return {
                ...step,
                subPageId,
                subPageOrder: i + 1,
                subPageName:
                  i > 0
                    ? step.name ||
                      `${
                        isSubOperation ? 'Suboperation' : 'Operation'
                      } ${order} - p.${i + 1}`
                    : step.name,
                depth: order.length - 1,
                order: order + `.${0.01 * (i + 1)}`,
              }
            })
          }

          return [
            {
              ...step,
              subPageId: null,
              name: step.name || `Operation ${order}`,
              depth: order.length - 1,
              order,
              subPageOrder: subPages.length > 0 ? 1 : undefined,
            },
          ]
        })
        .sort((a: any, b: any) => {
          const aParts = a.order.split('.').map(Number)
          const bParts = b.order.split('.').map(Number)

          const len = Math.max(aParts.length, bParts.length)
          for (let i = 0; i < len; i++) {
            const aVal = aParts[i] ?? 0
            const bVal = bParts[i] ?? 0
            if (aVal !== bVal) {
              return aVal - bVal
            }
          }
          return 0
        }),
    [steps],
  )

  const partsBom = useFullPartsBOM({
    onlyCountUsed: true,
  })
  const toolsBom = useFullToolsBOM({
    documentVersionId: data?.documentVersion?.id,
  })

  const partsBomChunks: Array<StaticTechPage> = useMemo(
    () =>
      toChunks({
        array: partsBom,
        chunkSize: MAX_PARTS_PER_PAGE,
      }).map((rows, i) => ({
        id: `parts-bom-${i}`,
        type: 'parts-bom',
        rows,
      })),
    [partsBom],
  )

  const toolsBomChunks: Array<StaticTechPage> = useMemo(
    () =>
      toChunks({
        array: toolsBom,
        chunkSize: MAX_PARTS_PER_PAGE,
      }).map((rows, i) => ({
        id: `tools-bom-${i}`,
        type: 'tools-bom',
        rows,
      })),
    [toolsBom],
  )

  const pages = [
    ...staticPages,
    ...partsBomChunks,
    ...toolsBomChunks,
    ...flattenedSteps,
  ] as Page[]

  return {
    isLoading,
    data: { pages },
  }
}

/**
 * Returns meta-data for the technician page
 */

export const useTechnicianDocument = () => {
  const currentPageId = useTechnicianPageStore((state) => state.currentPage)
  const {
    isLoading,
    data: { pages },
  } = useTechnicianPages()

  const currentPage = useCurrentPage({ currentPageId, pages })
  const nextPage = useNextPage({ currentPage, pages })
  const prevPage = usePrevPage({ currentPage, pages })
  const currentPageIndex = useCurrentPageIndex({ currentPage, pages })

  return {
    isLoading,
    prevPage,
    nextPage,
    currentPage,
    currentPageIndex,
    totalPages: staticPages.length + pages.length,
  }
}
